import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import FinancingBanner from '../../../components/feature/FinancingBanner';

export default function AgricultureServicesPage() {
  const dataTypes = [
    {
      icon: 'ri-leaf-line',
      title: 'NDVI Analysis',
      description: 'Normalized Difference Vegetation Index reveals plant health variations invisible to the naked eye, helping you spot stress before it spreads.'
    },
    {
      icon: 'ri-contrast-2-line',
      title: 'Multispectral Imaging',
      description: 'Capture data across multiple light wavelengths to detect nutrient deficiencies, disease pressure, and irrigation issues early.'
    },
    {
      icon: 'ri-camera-line',
      title: 'High-Resolution Imagery',
      description: 'Crystal-clear aerial photos that let you see every row, every plant, and every problem area across your entire operation.'
    },
    {
      icon: 'ri-temp-hot-line',
      title: 'Thermal Mapping',
      description: 'Identify water stress, drainage problems, and irrigation inefficiencies by detecting temperature variations across your fields.'
    },
    {
      icon: 'ri-map-2-line',
      title: 'Topographic Surveys',
      description: 'Detailed elevation data for drainage planning, land leveling, and understanding how water moves across your property.'
    },
    {
      icon: 'ri-bar-chart-box-line',
      title: 'Yield Prediction Models',
      description: 'Combine historical data with current field conditions to forecast yields and plan harvest logistics with confidence.'
    }
  ];

  const benefits = [
    {
      stat: '15-20%',
      label: 'Input Savings',
      description: 'Apply only what\'s needed, where it\'s needed. Variable rate prescriptions reduce waste and protect your margins.'
    },
    {
      stat: '3-5x',
      label: 'Faster Scouting',
      description: 'Cover hundreds of acres in hours instead of days. Find problems before they become expensive.'
    },
    {
      stat: '8-12%',
      label: 'Yield Improvement',
      description: 'Early detection and targeted intervention mean healthier crops and better returns at harvest.'
    },
    {
      stat: '100%',
      label: 'Field Coverage',
      description: 'Every acre, every row, every time. No more sampling and guessing—see the whole picture.'
    }
  ];

  const crops = [
    { name: 'Corn', icon: 'ri-seedling-line' },
    { name: 'Soybeans', icon: 'ri-plant-line' },
    { name: 'Wheat', icon: 'ri-leaf-line' },
    { name: 'Cotton', icon: 'ri-cloud-line' },
    { name: 'Rice', icon: 'ri-drop-line' },
    { name: 'Specialty Crops', icon: 'ri-flower-line' },
    { name: 'Orchards', icon: 'ri-tree-line' },
    { name: 'Vineyards', icon: 'ri-goblet-line' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="relative min-h-[600px] flex items-center">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/7ade7ca74f505a3de6bc75699734148e.jpg"
            alt="Aerial view of farmland"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-32 w-full">
          <p className="text-emerald-400 font-semibold tracking-wide mb-4">AGRICULTURE DRONE SERVICES</p>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 max-w-3xl leading-tight">
            See Your Fields Like Never Before
          </h1>
          <p className="text-xl text-gray-200 max-w-2xl mb-8 leading-relaxed">
            Aerial intelligence that helps you make smarter decisions, catch problems early, and get more from every acre. Real data. Real results. No guesswork.
          </p>
          <div className="flex flex-wrap gap-4">
            <a
              href="#assessment"
              className="bg-emerald-700 hover:bg-emerald-600 text-white font-bold px-8 py-4 rounded-md transition-all cursor-pointer whitespace-nowrap"
            >
              Schedule an Assessment
            </a>
            <a
              href="#data-types"
              className="bg-white/10 hover:bg-white/20 text-white font-bold px-8 py-4 rounded-md transition-all cursor-pointer whitespace-nowrap border border-white/30"
            >
              See What We Capture
            </a>
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-20 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Precision Agriculture Starts With Better Information
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Farming has always been about reading the land. Today, that means going beyond what you can see from the cab or walking the rows. Our drone services give you a complete, data-rich view of your operation—so you can act on facts, not hunches.
              </p>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                We collect high-resolution imagery, multispectral data, and thermal maps that reveal what's happening across every acre. From plant health to drainage patterns, from pest pressure to nutrient deficiencies—you'll see it all, clearly and quickly.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                This isn't about replacing your expertise. It's about giving you better tools to do what you already do well: grow crops and manage land.
              </p>
            </div>
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=Close%20up%20of%20professional%20agricultural%20drone%20flying%20low%20over%20green%20soybean%20field%20with%20multispectral%20sensors%20visible%20capturing%20crop%20health%20data%20warm%20afternoon%20light%20rural%20farming%20landscape%20background%20sharp%20focus%20on%20drone%20technology&width=600&height=500&seq=agri-drone-002&orientation=landscape"
                alt="Agricultural drone in action"
                className="w-full h-[500px] object-cover rounded-lg shadow-xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-emerald-700 text-white p-6 rounded-lg shadow-lg">
                <p className="text-3xl font-bold">500+</p>
                <p className="text-sm">Fields Mapped This Season</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Topo Divider */}
      <div className="topo-divider"></div>

      {/* Data Types Section */}
      <section id="data-types" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-emerald-700 font-semibold tracking-wide mb-3">WHAT WE CAPTURE</p>
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Data That Drives Better Decisions
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Every flight collects multiple layers of actionable information. Here's what you'll get—and how it helps. <a href="/blog/drone-maps-precision-agriculture" className="text-emerald-800 hover:text-emerald-700 font-semibold inline-flex items-center gap-1">Read our complete guide to drone mapping <i className="ri-arrow-right-line"></i></a>
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {dataTypes.map((item, index) => (
              <div
                key={index}
                className="bg-stone-50 p-8 rounded-lg hover:shadow-lg transition-all group"
              >
                <div className="w-14 h-14 flex items-center justify-center bg-emerald-100 rounded-lg mb-6 group-hover:bg-emerald-700 transition-colors">
                  <i className={`${item.icon} text-2xl text-emerald-800 group-hover:text-white transition-colors`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                <p className="text-gray-600 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Topo Divider */}
      <div className="topo-divider"></div>

      {/* Benefits Section */}
      <section className="py-20 bg-emerald-900 text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-emerald-300 font-semibold tracking-wide mb-3">THE IMPACT</p>
            <h2 className="text-4xl font-bold mb-6">
              How Aerial Data Improves Your Bottom Line
            </h2>
            <p className="text-lg text-emerald-100 max-w-3xl mx-auto">
              Better information leads to better decisions. Here's what farmers are seeing when they put aerial intelligence to work.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((item, index) => (
              <div key={index} className="text-center p-8 bg-white/5 rounded-lg">
                <p className="text-5xl font-bold text-emerald-300 mb-2">{item.stat}</p>
                <p className="text-lg font-semibold mb-4">{item.label}</p>
                <p className="text-emerald-200 text-sm leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Topo Divider */}
      <div className="topo-divider"></div>

      {/* How It Helps Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <img
                src="https://readdy.ai/api/search-image?query=Farmer%20reviewing%20colorful%20NDVI%20crop%20health%20map%20on%20tablet%20device%20standing%20in%20green%20cornfield%20with%20drone%20visible%20in%20background%20sky%20precision%20agriculture%20technology%20warm%20natural%20lighting%20professional%20agricultural%20consulting%20scene&width=600&height=500&seq=agri-farmer-003&orientation=landscape"
                alt="Farmer reviewing crop data"
                className="w-full h-[500px] object-cover rounded-lg shadow-xl"
              />
            </div>
            <div className="order-1 lg:order-2">
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                From Raw Data to Real Action
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                We don't just hand you files and walk away. Every flight comes with clear, actionable insights you can put to work immediately.
              </p>
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-emerald-100 rounded-lg flex-shrink-0">
                    <i className="ri-focus-3-line text-xl text-emerald-800"></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Spot Problems Early</h3>
                    <p className="text-gray-600">Catch disease, pest pressure, and nutrient issues before they spread—often weeks before they're visible from the ground.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-emerald-100 rounded-lg flex-shrink-0">
                    <i className="ri-map-pin-line text-xl text-emerald-800"></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Target Your Inputs</h3>
                    <p className="text-gray-600">Variable rate prescriptions based on actual field conditions. Apply what's needed, where it's needed—nothing more.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-emerald-100 rounded-lg flex-shrink-0">
                    <i className="ri-line-chart-line text-xl text-emerald-800"></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Track Progress Over Time</h3>
                    <p className="text-gray-600">Compare flights throughout the season to see how treatments are working and where adjustments are needed.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-emerald-100 rounded-lg flex-shrink-0">
                    <i className="ri-file-chart-line text-xl text-emerald-800"></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Document Everything</h3>
                    <p className="text-gray-600">Complete records for crop insurance, compliance, and your own planning. Every flight is logged and archived.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Topo Divider */}
      <div className="topo-divider"></div>

      {/* Crops We Serve */}
      <section className="py-20 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-emerald-700 font-semibold tracking-wide mb-3">CROPS & OPERATIONS</p>
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              We Work With All Types of Operations
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              From 200-acre family farms to 10,000-acre commercial operations. Row crops to orchards. If it grows, we can help you grow it better.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12">
            {crops.map((crop, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded-lg text-center hover:shadow-lg transition-all cursor-pointer group"
              >
                <div className="w-16 h-16 flex items-center justify-center bg-emerald-100 rounded-full mx-auto mb-4 group-hover:bg-emerald-700 transition-colors">
                  <i className={`${crop.icon} text-2xl text-emerald-800 group-hover:text-white transition-colors`}></i>
                </div>
                <p className="font-semibold text-gray-900">{crop.name}</p>
              </div>
            ))}
          </div>

          <div className="bg-white p-8 rounded-lg shadow-lg">
            <div className="grid md:grid-cols-3 gap-8 text-center">
              <div>
                <p className="text-4xl font-bold text-emerald-700 mb-2">200 - 50,000</p>
                <p className="text-gray-600">Acres Per Operation</p>
              </div>
              <div>
                <p className="text-4xl font-bold text-emerald-700 mb-2">Same Day</p>
                <p className="text-gray-600">Data Delivery Available</p>
              </div>
              <div>
                <p className="text-4xl font-bold text-emerald-700 mb-2">Seasonal</p>
                <p className="text-gray-600">Monitoring Packages</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Topo Divider */}
      <div className="topo-divider"></div>

      {/* Safety & Professionalism */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-emerald-700 font-semibold tracking-wide mb-3">SAFETY & PROFESSIONALISM</p>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Licensed, Insured, and Ready to Work
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Every pilot on our team holds FAA Part 107 certification and carries full liability insurance. We follow strict safety protocols on every flight—because your operation and our reputation depend on it.
              </p>
              <div className="grid grid-cols-2 gap-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-green-100 rounded-full">
                    <i className="ri-check-line text-xl text-green-600"></i>
                  </div>
                  <span className="font-semibold text-gray-900">FAA Part 107 Certified</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-green-100 rounded-full">
                    <i className="ri-check-line text-xl text-green-600"></i>
                  </div>
                  <span className="font-semibold text-gray-900">Fully Insured</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-green-100 rounded-full">
                    <i className="ri-check-line text-xl text-green-600"></i>
                  </div>
                  <span className="font-semibold text-gray-900">Pre-Flight Checklists</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-green-100 rounded-full">
                    <i className="ri-check-line text-xl text-green-600"></i>
                  </div>
                  <span className="font-semibold text-gray-900">Weather Monitoring</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-green-100 rounded-full">
                    <i className="ri-check-line text-xl text-green-600"></i>
                  </div>
                  <span className="font-semibold text-gray-900">Airspace Compliance</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-green-100 rounded-full">
                    <i className="ri-check-line text-xl text-green-600"></i>
                  </div>
                  <span className="font-semibold text-gray-900">Equipment Maintained</span>
                </div>
              </div>
            </div>
            <div>
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20drone%20pilot%20in%20safety%20vest%20preparing%20agricultural%20drone%20for%20flight%20in%20open%20farmland%20field%20with%20equipment%20case%20and%20tablet%20controller%20visible%20early%20morning%20golden%20light%20professional%20commercial%20drone%20operations%20scene&width=600&height=500&seq=agri-pilot-004&orientation=landscape"
                alt="Professional drone pilot"
                className="w-full h-[500px] object-cover rounded-lg shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Topo Divider */}
      <div className="topo-divider"></div>

      {/* Financing Section */}
      <FinancingBanner context="Precision agriculture services pay for themselves in better yields and lower input costs. Our financing options make it easy to get started — ask about 0% APR promotional plans." />

      {/* Topo Divider */}
      <div className="topo-divider"></div>

      {/* CTA Section */}
      <section id="assessment" className="py-20 bg-emerald-800">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to See Your Operation From Above?
          </h2>
          <p className="text-xl text-emerald-100 mb-8 max-w-2xl mx-auto">
            Schedule a field assessment and we'll show you exactly what aerial data can do for your farm. No pressure, no obligation—just a conversation about what's possible. Want to see potential savings first? <a href="/roi-calculator" className="text-white hover:text-emerald-200 font-semibold underline">Calculate your ROI</a>.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="/instant-quote"
              className="bg-white text-emerald-800 hover:bg-emerald-50 font-bold px-8 py-4 rounded-md transition-all cursor-pointer whitespace-nowrap"
            >
              Get an Instant Quote
            </a>
            <a
              href="tel:+15551234567"
              className="bg-emerald-700 hover:bg-emerald-600 text-white font-bold px-8 py-4 rounded-md transition-all cursor-pointer whitespace-nowrap border border-emerald-500"
            >
              <i className="ri-phone-line mr-2"></i>
              Call Us Directly
            </a>
          </div>
          <p className="text-emerald-200 mt-6 text-sm">
            Or email us at <a href="mailto:info@greenfieldpartners.com" className="underline hover:text-white">info@greenfieldpartners.com</a>
          </p>
        </div>
      </section>

      {/* Topo Divider */}
      <div className="topo-divider"></div>

      {/* Related Services */}
      <section className="py-20 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Related Services</h2>
            <p className="text-gray-600">Aerial imaging is just the start. Here's how we can help further.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <Link to="/services" className="bg-white p-8 rounded-lg shadow-md hover:shadow-xl transition-all group cursor-pointer">
              <div className="w-14 h-14 flex items-center justify-center bg-emerald-100 rounded-lg mb-6 group-hover:bg-emerald-700 transition-colors">
                <i className="ri-drop-line text-2xl text-emerald-800 group-hover:text-white transition-colors"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Precision Application</h3>
              <p className="text-gray-600">Targeted spraying based on the data we collect. Apply exactly what's needed, where it's needed.</p>
            </Link>
            <Link to="/industries" className="bg-white p-8 rounded-lg shadow-md hover:shadow-xl transition-all group cursor-pointer">
              <div className="w-14 h-14 flex items-center justify-center bg-emerald-100 rounded-lg mb-6 group-hover:bg-emerald-700 transition-colors">
                <i className="ri-landscape-line text-2xl text-emerald-800 group-hover:text-white transition-colors"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Land Management</h3>
              <p className="text-gray-600">Timber surveys, conservation mapping, and property assessments for landowners and managers.</p>
            </Link>
            <Link to="/how-we-work" className="bg-white p-8 rounded-lg shadow-md hover:shadow-xl transition-all group cursor-pointer">
              <div className="w-14 h-14 flex items-center justify-center bg-emerald-100 rounded-lg mb-6 group-hover:bg-emerald-700 transition-colors">
                <i className="ri-route-line text-2xl text-emerald-800 group-hover:text-white transition-colors"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Our Process</h3>
              <p className="text-gray-600">Learn how we work with you from first contact to completed project and ongoing support.</p>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
